import { useAppSelector } from "@/stores/hook";
import type { RootState } from "@/stores/store";
import { deriveUserContext, type RbacUserContext } from "@/rbac/utils";

const ADMIN_SYSTEM_ROLES = new Set(["SYSTEM_ADMIN", "TENANT_ADMIN", "PLATFORM_STAFF"]);

const isAdmin = (ctx: RbacUserContext | null | undefined): boolean =>
  !!ctx && (ctx.isTenantAdmin || ctx.isSystemAdmin || ADMIN_SYSTEM_ROLES.has(String(ctx.systemRole ?? "")));

/**
 * Share as PDF = an export of what the page shows, so it rides the server's
 * `ReportPolicy.canExport()`: admins always; everyone else unless their role
 * says `bulk_operations.report_export: "deny"` (default ALLOW — report
 * figures are aggregates the viewer already sees). The upload route enforces
 * the same rule; this only hides a button that would 403.
 */
const selectCanShareReports = (state: RootState): boolean => {
  if (isAdmin(state.rbac.context) || isAdmin(deriveUserContext(state.rbac.currentUser))) return true;
  const bulk = state.rbac.accessConfig?.global?.bulk_operations as Record<string, unknown> | undefined;
  return bulk?.report_export !== "deny";
};

export function useCanShareReports(): boolean {
  return useAppSelector(selectCanShareReports);
}

/** Display name for the PDF footer ("Prepared by …"). */
export function useReportPreparedBy(): string | null {
  return useAppSelector((state) => {
    const u = state.rbac.currentUser as Record<string, unknown> | null;
    if (!u) return null;
    const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
    const full = [text(u.firstName), text(u.lastName)].filter(Boolean).join(" ");
    return text(u.name) || text(u.fullName) || full || text(u.email) || null;
  });
}
